import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { isApiError } from "../api/client";

interface Toast {
  id: number;
  tone: "error" | "success" | "info";
  message: string;
  requestId?: string | null;
}

interface ToastApi {
  show: (t: Omit<Toast, "id">) => void;
  error: (e: unknown, fallback?: string) => void;
  success: (message: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts((ts) => ts.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (t: Omit<Toast, "id">) => {
      const id = nextId++;
      setToasts((ts) => [...ts.slice(-3), { ...t, id }]);
      window.setTimeout(() => dismiss(id), t.tone === "error" ? 9000 : 4500);
    },
    [dismiss],
  );
  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message) => show({ tone: "success", message }),
      error: (e, fallback = "Something went wrong.") =>
        show({
          tone: "error",
          message: isApiError(e) ? e.message : fallback,
          requestId: isApiError(e) ? e.requestId : null,
        }),
    }),
    [show],
  );
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`} role={t.tone === "error" ? "alert" : "status"}>
            <div style={{ flex: 1 }}>
              <p>{t.message}</p>
              {t.requestId && <p className="ref">Reference: {t.requestId}</p>}
            </div>
            <button className="btn ghost small" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ×
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v;
}
